<template>
    <div class="register-form-container">
        <form class="register-form" novalidate @submit.stop.prevent="register">
            <div class="name-fields">
                <md-input-container :class="{'md-input-invalid': firstNameError()}">
                    <label for="register-first-name">First Name</label>
                    <md-input 
                        id="register-first-name"
                        v-model="newUser.firstName" 
                        required
                        @blur="validateName('firstName')"
                    ></md-input>
                    <span class="md-error" v-if="firstNameError">{{ errorMessage }}</span>
                </md-input-container>
    
                <md-input-container :class="{'md-input-invalid': lastNameError()}">
                    <label for="register-last-name">Last Name</label>
                    <md-input 
                        id="register-last-name"
                        v-model="newUser.lastName" 
                        required
                        @blur="validateName('lastName')"
                    ></md-input>
                    <span class="md-error" v-if="lastNameError">{{ errorMessage }}</span>
                </md-input-container>
            </div>
    
            <md-input-container :class="{'md-input-invalid': emailError()}">
                <label for="register-email">Email Address</label>
                <md-input 
                    id="register-email"
                    v-model="newUser.email" 
                    type="email" 
                    required
                    @blur="validateEmail"
                ></md-input>
                <span class="md-error" v-if="emailError">{{ errorMessage }}</span>
            </md-input-container>
    
            <md-input-container :class="{'md-input-invalid': passwordError()}">
                <label for="register-password">Password</label>
                <md-input 
                    id="register-password"
                    v-model="newUser.password" 
                    type="password" 
                    required
                    @input="validatePassword"
                ></md-input>
                <span class="md-error" v-if="passwordError">{{ errorMessage }}</span>
            </md-input-container>
    
            <div class="password-requirements" v-if="newUser.password && !passwordValidation.isValid">
                <div class="requirement-item" :class="{'requirement-met': passwordValidation.requirements.minLength}">
                    <md-icon class="requirement-icon">check_circle</md-icon>
                    <span>At least 8 characters</span>
                </div>
                <div class="requirement-item" :class="{'requirement-met': passwordValidation.requirements.hasUpperCase}">
                    <md-icon class="requirement-icon">check_circle</md-icon>
                    <span>One uppercase letter</span>
                </div>
                <div class="requirement-item" :class="{'requirement-met': passwordValidation.requirements.hasLowerCase}">
                    <md-icon class="requirement-icon">check_circle</md-icon>
                    <span>One lowercase letter</span>
                </div>
                <div class="requirement-item" :class="{'requirement-met': passwordValidation.requirements.hasNumbers}">
                    <md-icon class="requirement-icon">check_circle</md-icon>
                    <span>One number</span>
                </div>
                <div class="requirement-item" :class="{'requirement-met': passwordValidation.requirements.hasSpecialChar}">
                    <md-icon class="requirement-icon">check_circle</md-icon>
                    <span>One special character</span>
                </div>
            </div>
    
            <md-input-container :class="{'md-input-invalid': confirmPasswordError()}">
                <label for="register-confirm-password">Confirm Password</label>
                <md-input 
                    id="register-confirm-password"
                    v-model="newUser.confirmPassword" 
                    type="password" 
                    required
                    @input="validatePasswordMatch"
                ></md-input>
                <span class="md-error" v-if="confirmPasswordError">Passwords do not match</span>
            </md-input-container>
    
            <!-- Marketing consent, INSIDE the card and directly above the button,
                 which is the placement that works for people who get this far.
                 ⚠ It is only half the story: this form is never rendered for the
                 46% who register with Google, so the dashboard banner catches
                 everyone else. See lib/marketingConsent.js.
                 An UNTICKED box writes NOTHING -- see register() -- so it falls
                 through to that banner rather than counting as a refusal. -->
            <label class="consent-row" for="register-marketing-consent">
                <input
                    id="register-marketing-consent"
                    type="checkbox"
                    class="consent-box"
                    v-model="marketingConsent"
                />
                <span class="consent-copy">
                    {{ consentText }}
                    <span class="consent-sub">{{ consentSubtext }}</span>
                </span>
            </label>

            <p class="register-error" role="alert" v-if="generalErrorMessage">{{ generalErrorMessage }}</p>

            <md-button 
                class="md-raised md-primary register-button" 
                @click="register" 
                :disabled="loading || !isFormValid"
            >
                <md-spinner v-if="loading" :md-size="20" class="md-accent"></md-spinner>
                <span v-else>Create Account</span>
            </md-button>
            <div class="login-link">
                <span>Already have an account?</span>
                <md-button 
                    class="signup-link" 
                    @click="toggleRegister"
                    flat
                >
                    Sign in
                </md-button>
            </div>
        </form>
    </div>
    </template>
    
    <script>
    import firebase from 'firebase/app';
    import { CONSENT_TEXT, CONSENT_SUBTEXT } from '@/lib/marketingConsent';
    import registerErrorMessage from '@/lib/register-error-message';
    import { signupNameFields } from '@/lib/user-name';
    
    export default {
        name: 'RegisterForm',
        data() {
            return {
                newUser: {
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    confirmPassword: ''
                },
                marketingConsent: false,
                consentText: CONSENT_TEXT,
                consentSubtext: CONSENT_SUBTEXT,
                hasError: '',
                errorMessage: '',
                loading: false,
                passwordValidation: {
                    isValid: false,
                    requirements: {
                        minLength: false,
                        hasUpperCase: false,
                        hasLowerCase: false,
                        hasNumbers: false,
                        hasSpecialChar: false
                    }
                }
            };
        },
        computed: {
            // Errors that aren't about one field (sign-up switched off, no connection, ...) used to show nowhere.
            generalErrorMessage() {
                return registerErrorMessage(this.hasError);
            },
            isFormValid() {
                const firstNameValid = this.newUser.firstName && this.newUser.firstName.trim().length >= 2;
                const lastNameValid = this.newUser.lastName && this.newUser.lastName.trim().length >= 2;
                const emailValid = this.newUser.email;
                const passwordValid = this.passwordValidation.isValid;
                const confirmPasswordValid = this.newUser.confirmPassword && this.newUser.password === this.newUser.confirmPassword;
    
                return firstNameValid &&
                       lastNameValid &&
                       emailValid &&
                       passwordValid &&
                       confirmPasswordValid;
            }
        },
        methods: {
            // ⚠ `+` IS VALID IN AN EMAIL ADDRESS. It was missing from the
            // separator class, so `name+cuelist@gmail.com` was refused at signup
            // with "your address has special characters" -- our own pattern
            // turning people away, not Firebase, which accepts it happily.
            //
            // Plus-addressing is a common habit among privacy-minded and
            // technical people, which describes a lot of lighting and sound. And
            // it was invisible: those people never became accounts, so nothing in
            // the data showed it happening. Only email signups were affected --
            // the Google button skips this check entirely, and that is 46% of
            // accounts.
            //
            // This check is belt-and-braces over Firebase Auth's own validation.
            // Keep it permissive: every character it wrongly excludes is a
            // customer who cannot sign up and is told it is their fault.
            isValidEmailFormat(email) {
                const pattern = /^[a-zA-Z0-9]+([._+-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                return pattern.test(email);
            },
            validateName(field) {
                const name = this.newUser[field] ? this.newUser[field].trim() : '';
    
                if (!name) {
                    this.hasError = `invalid-${field}`;
                    this.errorMessage = `${field === 'firstName' ? 'First' : 'Last'} name is required`;
                    return false;
                }
    
                if (name.length < 1) {
                    this.hasError = `invalid-${field}`;
                    this.errorMessage = `${field === 'firstName' ? 'First' : 'Last'} name must be at least 1 characters`;
                    return false;
                }
    
                this.newUser[field] = name;
                if (this.hasError === `invalid-${field}`) {
                    this.hasError = '';
                    this.errorMessage = '';
                }
                return true;
            },
            validateEmail() {
                const email = this.newUser.email ? this.newUser.email.trim() : '';
                if (!email) {
                    this.hasError = 'auth/invalid-email';
                    this.errorMessage = 'Email is required';
                    return false;
                }
                this.hasError = '';
                this.errorMessage = '';
                return true;
            },
            validatePassword() {
                const password = this.newUser.password;
                this.passwordValidation = {
                    isValid: false,
                    requirements: {
                        minLength: password.length >= 8,
                        hasUpperCase: /[A-Z]/.test(password),
                        hasLowerCase: /[a-z]/.test(password),
                        hasNumbers: /\d/.test(password),
                        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
                    }
                };
    
                this.passwordValidation.isValid = Object.values(this.passwordValidation.requirements).every(Boolean);
                return this.passwordValidation.isValid;
            },
            validatePasswordMatch() {
                return this.newUser.password === this.newUser.confirmPassword;
            },
            register() {
                this.hasError = '';
                this.errorMessage = '';
                this.loading = true;
                if (!this.isValidEmailFormat(this.newUser.email)) {
                    this.loading = false;
                    this.hasError = 'auth/invalid-email';
                    // Says what to do, and does not accuse them of doing
                    // something wrong with an address that may be perfectly
                    // valid.
                    this.errorMessage = 'That email address does not look right. Please check it and try again.';
                    return;
                }
    
                firebase.auth().createUserWithEmailAndPassword(this.newUser.email, this.newUser.password)
                .then(({user}) => {
                    // First and last name are saved separately too (Sun 25), so a
                    // last-name sort is exact for new accounts. See lib/user-name.js.
                    const names = signupNameFields(this.newUser.firstName, this.newUser.lastName);
                    user.updateProfile({
                        displayName: names.username
                    }).then(() => {
                        user.signInMethod = 'email';
                        // Pass the tick through. ⚠ Only `true` means anything:
                        // an unticked box is the ABSENCE of an answer, not a
                        // refusal, and createUser writes nothing for it so the
                        // dashboard banner still gets its turn.
                        this.$emit('success', user, this.marketingConsent,
                            { firstName: names.firstName, lastName: names.lastName });
                    });
    
                    // actionCodeSettings.url gives the verification page a "Continue"
                    // link back into the app (otherwise the user is stranded on the
                    // success page). window.location.origin keeps it env-correct
                    // (sandbox/staging/prod) and is an already-authorized domain.
                    user.sendEmailVerification({
                        url: `${window.location.origin}/login`,
                        handleCodeInApp: false
                    }).then(() => {
                        // Email verification sent to user
                    });
                    this.loading = false;
                }).catch((error) => {
                    console.error(error);
                    this.loading = false;
                    this.hasError = error.code;
                    this.errorMessage = error.message;
                });
            },

            getErrorMessage(errorCode) {
                switch (errorCode) {
                case 'auth/email-already-in-use':
                    return 'This email is already registered. Please sign in or use a different email.';
                case 'auth/invalid-email':
                    return 'Please enter a valid email address.';
                case 'invalid-firstName':
                    return 'Please enter a valid first name.';
                case 'invalid-lastName':
                    return 'Please enter a valid last name.';
                case 'auth/weak-password':
                    return 'Password is too weak. Please follow the password requirements.';
                default:
                    return 'An error occurred. Please try again.';
                }
            },
            emailError() {
                return this.hasError === 'auth/invalid-email' ||
                       this.hasError === 'auth/email-already-in-use';
            },
            passwordError() {
                return this.hasError === 'auth/weak-password';
            },
            confirmPasswordError() {
                return this.newUser.confirmPassword && 
                       this.newUser.password !== this.newUser.confirmPassword;
            },
            firstNameError() {
                return this.hasError === 'invalid-firstName';
            },
            lastNameError() {
                return this.hasError === 'invalid-lastName';
            },
            toggleRegister() {
                this.$emit('toggle-register');
            }
        }
    };
    </script>
    
    
<style lang="scss" scoped>
.register-form-container {
    max-width: 500px;
    margin: 0 auto;
}

.register-form {
    background: white;
    border-radius: 8px;

    h2 {
        text-align: center;
        color: #333;
        margin-bottom: 10px;
    }

    .form-description {
        text-align: center;
        color: #666;
        margin-bottom: 30px;
    }
}

.name-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
}

.password-requirements {
    margin: 12px 0;
    padding: 12px;
    background-color: #f5f5f5;
    border-radius: 4px;
    border-left: 4px solid #2196F3;
    
    .requirement-item {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        color: #666;
        transition: all 0.3s ease;
        
        &:last-child {
            margin-bottom: 0;
        }
        
        .requirement-icon {
            margin-right: 8px;
            color: #9e9e9e;
            font-size: 24px;
        }
        
        &.requirement-met {
            color: #4CAF50;
            
            .requirement-icon {
                color: #4CAF50;
            }
        }
    }
}

.register-error {
    margin: 16px 0 0;
    color: #d32f2f;
    font-size: 14px;
    line-height: 1.4;
}

.register-button {
    width: 100%;
    height: 48px;
    font-size: 16px;
    margin: 20px 0;
    text-transform: none;
}

.login-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 14px;
    color: #666;
    width: 100%;
    .signup-link {
        color: #2196F3;
        text-transform: none;
        font-weight: 500;
        background: none;
        box-shadow: none;
        min-width: unset;
        padding: 0;
        &:hover {
            text-decoration: underline;
            background: none;
        }
    }
}

.md-error {
    color: #f44336;
    font-size: 12px;
    margin-top: 5px;
}

.md-input-container {
    margin-bottom: 20px;
}

/* Consent, inside the card and directly above Create Account. Bordered and
   tinted rather than a bare checkbox: this is the one control on the form that
   is optional, so it has to earn a glance rather than blend into the fields. */
.consent-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 4px 0 18px;
    padding: 11px 12px;
    border: 1px solid #dfe4ea;
    border-radius: 6px;
    background: #fafbfc;
    cursor: pointer;
    transition: border-color .15s, background .15s;
}
.consent-row:hover { border-color: #73d5e3; background: #f6fdfe; }
.consent-box {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin: 1px 0 0;
    accent-color: #12718a;
    cursor: pointer;
}
.consent-copy {
    font-size: 12.5px;
    line-height: 1.45;
    color: #3d4854;
}
.consent-copy .consent-sub {
    display: block;
    color: #8a939c;
    font-size: 11px;
    margin-top: 2px;
}
</style>


// WEBPACK FOOTER //
// register-form.vue?3e24c115